import { Component, OnInit } from '@angular/core';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { EndUser } from '@app/core/interfaces/c5/end-user';
import { FeatureName } from '@app/core/interfaces/c5/feature-name';
import { EndUserData } from '@app/core/interfaces/end-user-data';
import { EnterpriseOptionType } from '@app/core/interfaces/enterprise-option-type';
import { SecurityPermissionName } from '@app/core/models/permission';
import { SubNavBaseComponent } from '@app/menu/components/sub-nav-base-component';
import { CallCenterEndUserContext } from '@app/shared/models/call-center-end-user-context';
import { EndUserContextService } from '@app/shared/services/context/end-user-context.service';
import { EnterpriseAuthorizationService } from '@app/shared/services/enterprise-authorization.service';
import { Authorization } from '@business/end-user/models/authorization';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'portal-enterprise-end-user-sub-nav',
  templateUrl: './enterprise-end-user-sub-nav.component.html',
  standalone: false,
})
export class EnterpriseEndUserSubNavComponent extends SubNavBaseComponent implements OnInit {
  public context$: Observable<SharedEndUserData>;
  public hasDevice$: Observable<boolean>;
  public canUseRichPresence$: Observable<boolean>;
  public hasMs365Presence$: Observable<boolean>;
  public user$: Observable<EndUser>;
  public Permission = SecurityPermissionName;

  public loading: boolean = true;

  constructor(
    private enterpriseAuthorizationService: EnterpriseAuthorizationService,
    private endUserContextService: EndUserContextService,
    private authenticationService: AuthenticationService
  ) {
    super();
  }

  public ngOnInit() {
    this.context$ = this.endUserContextService.endUser.data$.pipe(untilDestroyed(this));

    this.endUserContextService.endUser.loading$.pipe(untilDestroyed(this)).subscribe(() => {
      this.loading = this.endUserContextService.endUser.snapshot == null;
    });

    this.user$ = this.context$.pipe(
      map((context) => context.user),
      filter((user) => user !== undefined),
      untilDestroyed(this)
    );

    this.hasDevice$ = this.user$.pipe(
      map((user) => user.auth === Authorization.FULL),
      untilDestroyed(this)
    );

    this.hasMs365Presence$ = combineLatest([
      this.endUserContextService.endUser.data$,
      this.enterpriseAuthorizationService.hasEnterpriseOption$(
        EnterpriseOptionType.Microsoft365PresenceSyncFeatures
      ),
    ]).pipe(
      map(
        ([endUser, hasEnterpriseOption]) =>
          hasEnterpriseOption &&
          endUser.user?.features?.some((feature) => feature.featName === FeatureName.MS_365_PRESENCE) &&
          this.authenticationService.hasPermission(SecurityPermissionName.VIEW_MS365_PRESENCE)
      ),
      untilDestroyed(this)
    );

    this.canUseRichPresence$ = this.endUserContextService.canUseRichPresence$().pipe(untilDestroyed(this));

    this.user$.subscribe(() => {
      this.loading = false;
    });
  }
}

interface SharedEndUserData extends EndUserData {
  callCenter?: CallCenterEndUserContext;
}
